/**
 * Backend-driven option lists for the Booking-module enum dropdowns.
 *
 * Served under the `options` block on the settings reads (BE answer
 * DONE-2026-06-23-booking-dropdown-option-endpoints): `settings/booking/` →
 * booking origin / customer type / booking status; `settings/payment/` →
 * payment type / method; `settings/cancellation/` → cancel reason / refund
 * method. Fetched by `getBookingEnumsSSR` and hydrated into the `bookings`
 * slice; each dropdown renders the slice list, falling back to its FE constant
 * (via {@link optionsOr}) when the list is absent (e.g. the user lacks the
 * settings-read permission, or before hydration).
 *
 * Notification Method is intentionally NOT here — it has no backend enum (BE
 * answer §5); it stays on its FE `NOTIFICATION_OPTIONS` constant.
 */

import type { SelectOption } from "@/Shared/CSelect/CSelect";

export interface BookingEnumOptions {
  bookingOrigin: SelectOption[];
  customerType: SelectOption[];
  bookingStatus: SelectOption[];
  paymentType: SelectOption[];
  paymentMethod: SelectOption[];
  cancelReason: SelectOption[];
  refundMethod: SelectOption[];
}

export const EMPTY_BOOKING_ENUMS: BookingEnumOptions = {
  bookingOrigin: [],
  customerType: [],
  bookingStatus: [],
  paymentType: [],
  paymentMethod: [],
  cancelReason: [],
  refundMethod: [],
};

/**
 * The backend list when it has entries, otherwise the FE fallback constant.
 * `fallback` is `readonly` so `as const` option arrays pass without a spread at
 * the call site; the result is a fresh mutable `SelectOption[]`.
 */
export function optionsOr(
  backend: SelectOption[] | undefined,
  fallback: readonly SelectOption[],
): SelectOption[] {
  return backend && backend.length > 0 ? backend : [...fallback];
}
